import { getAuthToken } from "../../../utils/auth";

const baseUrl = process.env.REACT_APP_API_URL;

export const MEDIA_CATEGORIES = [
    { value: "FREE_TIME_LISTENING", label: "Free time listening" },
    { value: "AUDIO_BANK", label: "Audio bank" },
    { value: "EVERY_DAY_ENLGISH", label: "Everyday English" },
    { value: "GRAMMAR_VIDEO", label: "Grammar video" },
];

export const TEST_TYPES = [
    { value: "CHECK_YOURSELF", label: "Check yourself" },
    { value: "VOCABULARY_CHALLENGE", label: "Vocabulary challenge" },
];

export const isVideoCategory = (category) => category === "GRAMMAR_VIDEO";

/** GET an API path and return `data` from the GenericResponse. */
export async function apiGet(path, params = {}) {
    const url = new URL(`${baseUrl}${path}`);
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
            url.searchParams.append(key, value);
        }
    });

    const res = await fetch(url, {
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "Content-Type": "application/json",
        },
    });

    const result = await res.json().catch(() => null);
    if (!res.ok || !result?.status) {
        throw new Error(errorMessage(result, res.status));
    }
    return result.data;
}

const errorMessage = (result, status) =>
    result?.apiError?.error ||
    [...(result?.apiError?.validationErrors || [])].join(", ") ||
    result?.message ||
    `Request failed (${status})`;

/** Sends a JSON body (POST/PUT) and returns `data` from the GenericResponse. */
export async function apiSendJson(method, path, body) {
    const res = await fetch(`${baseUrl}${path}`, {
        method,
        headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
    });

    const result = await res.json().catch(() => null);
    if (!res.ok || !result?.status) {
        throw new Error(errorMessage(result, res.status));
    }
    return result.data;
}

/** URL for previewing any media file by its path under the media root (admins only). */
export async function getFilePreviewUrl(path) {
    const token = await apiGet("/admin/content/media-files/stream-token", { path });
    const url = new URL(`${baseUrl}/media/admin-files`);
    url.searchParams.append("path", path);
    url.searchParams.append("token", token);
    return url.toString();
}

/**
 * Sends multipart form data and reports upload progress (0–100). Uses XHR
 * because fetch has no upload progress events.
 */
export function apiSendForm(path, formData, onProgress) {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${baseUrl}${path}`);
        xhr.setRequestHeader("Authorization", `Bearer ${getAuthToken()}`);

        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable && onProgress) {
                onProgress(Math.round((event.loaded / event.total) * 100));
            }
        };

        xhr.onload = () => {
            let result = null;
            try {
                result = JSON.parse(xhr.responseText);
            } catch (e) {
                // Non-JSON error page, e.g. from a proxy rejecting a large upload.
            }
            if (xhr.status >= 200 && xhr.status < 300 && result?.status) {
                resolve(result.data);
            } else if (xhr.status === 413) {
                reject(new Error("The file is too large for the server."));
            } else {
                reject(new Error(errorMessage(result, xhr.status)));
            }
        };
        xhr.onerror = () => reject(new Error("Network error while uploading."));

        xhr.send(formData);
    });
}

/**
 * <audio>/<video> can't send the Authorization header, so the file URL carries
 * a short-lived media token instead.
 */
export async function getMediaFileUrl(mediaItemId) {
    const token = await apiGet("/media/stream-token", { mediaItemId });
    const url = new URL(`${baseUrl}/media/files`);
    url.searchParams.append("mediaItemId", mediaItemId);
    if (token) url.searchParams.append("mediaToken", token);
    return url.toString();
}
